import React, { useState, useEffect } from 'react';
import { Building2, CheckCircle2, Wallet, AlertTriangle } from 'lucide-react';
import useIdleLogout from '../../hooks/useIdleLogout';
import useBackButtonLogout from '../../hooks/useBackButtonLogout';
import useIsDesktop from '../../hooks/useIsDesktop';
import { API_BASE } from '../../config';
import AppShell from '../../layout/AppShell';
import { landlordNav } from '../../layout/navConfig';
import StatCard from '../../components/ui/StatCard';
import SectionCard from '../../components/ui/SectionCard';
import DataTable from '../../components/ui/DataTable';
import StatusBadge from '../../components/ui/StatusBadge';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function LandlordDashboard() {

  useIdleLogout(5);
  useBackButtonLogout();

  const [landlord, setLandlord]         = useState(null);
  const [properties, setProperties]     = useState([]);
  const [units, setUnits]               = useState([]);
  const [tenants, setTenants]           = useState([]);
  const [allPayments, setAllPayments]   = useState([]);
  const [allExpenses, setAllExpenses]   = useState([]);
  const [waterHistory, setWaterHistory] = useState([]);
  const [manualUnitId, setManualUnitId]   = useState('');
  const [manualAmount, setManualAmount]   = useState('');
  const [manualMethod, setManualMethod]   = useState('mpesa');
  const [manualPhone, setManualPhone]     = useState('');
  const [manualMpesaCode, setManualMpesaCode] = useState('');
  const [manualDate, setManualDate]       = useState(() => new Date().toISOString().slice(0, 10));
  const [manualMonth, setManualMonth]     = useState(() => {
    const d = new Date();
    return `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
  });
  const [submittingManual, setSubmittingManual] = useState(false);
  const [manualError, setManualError]     = useState('');
  const [agents, setAgents]             = useState([]);
  const [pendingAgents, setPendingAgents] = useState([]);
  const [editingDisplayName, setEditingDisplayName] = useState(false);
  const [displayNameInput, setDisplayNameInput]     = useState('');
  const [savingDisplayName, setSavingDisplayName]   = useState(false);
  const [editingPhone, setEditingPhone]   = useState(false);
  const [phoneInput, setPhoneInput]       = useState('');
  const [savingPhone, setSavingPhone]     = useState(false);
  const [editingEmail, setEditingEmail]   = useState(false);
  const [emailInput, setEmailInput]       = useState('');
  const [savingEmail, setSavingEmail]     = useState(false);
  const [editingIdNumber, setEditingIdNumber] = useState(false);
  const [idNumberInput, setIdNumberInput]     = useState('');
  const [savingIdNumber, setSavingIdNumber]   = useState(false);
  const [newAgentName, setNewAgentName]         = useState('');
  const [newAgentUsername, setNewAgentUsername] = useState('');
  const [newAgentPhone, setNewAgentPhone]       = useState('');
  const [newAgentIdNumber, setNewAgentIdNumber] = useState('');
  const [newAgentPassword, setNewAgentPassword] = useState('');
  const [creatingAgent, setCreatingAgent]       = useState(false);
  const [createAgentError, setCreateAgentError] = useState('');
  const [editingAgentId, setEditingAgentId]     = useState(null);
  const [editAgentName, setEditAgentName]       = useState('');
  const [editAgentPhone, setEditAgentPhone]     = useState('');
  const [editAgentIdNumber, setEditAgentIdNumber] = useState('');
  const [editAgentEmail, setEditAgentEmail]     = useState('');
  const [savingAgentEdit, setSavingAgentEdit]   = useState(false);
  const [caretakers, setCaretakers]                       = useState([]);
  const [caretakerTemplate, setCaretakerTemplate]         = useState('');
  const [newCaretakerName, setNewCaretakerName]           = useState('');
  const [newCaretakerPhone, setNewCaretakerPhone]         = useState('');
  const [newCaretakerIdNumber, setNewCaretakerIdNumber]   = useState('');
  const [newCaretakerPhoto, setNewCaretakerPhoto]         = useState(null);
  const [newCaretakerAgreed, setNewCaretakerAgreed]       = useState(false);
  const [creatingCaretaker, setCreatingCaretaker]         = useState(false);
  const [createCaretakerError, setCreateCaretakerError]   = useState('');
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [activePage, setActivePage]     = useState('dashboard');
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(`${MONTH_NAMES[now.getMonth()]} ${now.getFullYear()}`);
  const toISODate = (d) => d.toISOString().slice(0, 10);
  const [reportStartDate, setReportStartDate] = useState(toISODate(new Date(now.getFullYear(), now.getMonth(), 1)));
  const [reportEndDate, setReportEndDate]     = useState(toISODate(now));
  const isDesktop = useIsDesktop();

  useEffect(() => {
    async function loadData() {
      try {
        const meRes = await fetch(`${API_BASE}/api/auth/me`, { credentials: 'include' });
        if (meRes.status === 401) {
          window.location.href = '/login';
          return;
        }
        const me = await meRes.json();
        if (meRes.ok) setLandlord(me);

        // All of these are independent of each other (own fetch, own state
        // setter) except water bills below, which needs the units response
        // - so they fire concurrently instead of one full round trip at a
        // time. Awaiting them in series previously stacked the full
        // network latency once per call, which is what made the dashboard
        // feel slow to load on a real mobile connection even though each
        // backend response is fast on its own.
        const [
          propertiesRes, propertiesData,
          unitsRes, unitsData,
          tenantsRes, tenantsData,
          paymentsRes, paymentsData,
          expensesRes, expensesData,
          agentsRes, agentsData,
          pendingAgentsRes, pendingAgentsData,
          caretakersRes, caretakersData,
          caretakerTemplateRes, caretakerTemplateData,
        ] = (await Promise.all([
          fetch(`${API_BASE}/api/properties`, { credentials: 'include' }).then(async (r) => [r, await r.json()]),
          fetch(`${API_BASE}/api/units`, { credentials: 'include' }).then(async (r) => [r, await r.json()]),
          fetch(`${API_BASE}/api/tenants`, { credentials: 'include' }).then(async (r) => [r, await r.json()]),
          fetch(`${API_BASE}/api/payments`, { credentials: 'include' }).then(async (r) => [r, await r.json()]),
          fetch(`${API_BASE}/api/expenses`, { credentials: 'include' }).then(async (r) => [r, await r.json()]),
          fetch(`${API_BASE}/api/agents`, { credentials: 'include' }).then(async (r) => [r, await r.json()]),
          fetch(`${API_BASE}/api/agents/pending`, { credentials: 'include' }).then(async (r) => [r, await r.json()]),
          fetch(`${API_BASE}/api/caretakers`, { credentials: 'include' }).then(async (r) => [r, await r.json()]),
          fetch(`${API_BASE}/api/caretakers/agreement-template`, { credentials: 'include' }).then(async (r) => [r, await r.json()]),
        ])).flat();

        if (propertiesRes.ok) {
          // GET /api/properties is public (registration/pay pages need the
          // full list), so scope it down to just the property this landlord owns
          setProperties(
            me && me.assigned_property_id
              ? propertiesData.filter((p) => p.property_id === me.assigned_property_id)
              : []
          );
        }
        if (unitsRes.ok) setUnits(unitsData);
        if (tenantsRes.ok) setTenants(tenantsData);
        if (paymentsRes.ok) setAllPayments(paymentsData);
        if (expensesRes.ok) setAllExpenses(expensesData);
        if (agentsRes.ok) setAgents(agentsData);
        if (pendingAgentsRes.ok) setPendingAgents(pendingAgentsData);
        if (caretakersRes.ok) setCaretakers(caretakersData);
        if (caretakerTemplateRes.ok) setCaretakerTemplate(caretakerTemplateData.content);

        const waterUnits = unitsRes.ok ? unitsData.filter((u) => u.has_water_bill) : [];
        if (waterUnits.length > 0) {
          const responses = await Promise.all(
            waterUnits.map((u) => fetch(`${API_BASE}/api/water-bills/${u.unit_id}`, { credentials: 'include' }))
          );
          if (!responses.some((r) => !r.ok)) {
            const dataByUnit = await Promise.all(responses.map((r) => r.json()));
            const flat = [];
            waterUnits.forEach((u, i) => {
              dataByUnit[i].forEach((b) => flat.push({ ...b, unit_number: u.unit_number }));
            });
            flat.sort((a, b) => b.bill_id - a.bill_id);
            setWaterHistory(flat);
          }
        }
      } catch (err) {
        setError('Could not load dashboard.');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const availableMonths = [...new Set(allPayments.map((p) => p.month))];
  if (!availableMonths.includes(selectedMonth)) availableMonths.unshift(selectedMonth);

  const monthPayments = allPayments.filter((p) => p.month === selectedMonth);
  const monthExpenses = allExpenses.filter((e) => {
    const parts = selectedMonth.split(' ');
    const year = parts[parts.length - 1];
    return e.expense_date && e.expense_date.startsWith(year);
  });

  const expectedIncome  = units.reduce((sum, u) => sum + u.rent_amount, 0);
  const collectedIncome = monthPayments.filter(p => p.status === 'paid').reduce((sum, p) => sum + p.amount, 0);
  const outstanding     = expectedIncome - collectedIncome;
  const totalExpenses   = monthExpenses.reduce((sum, e) => sum + e.amount, 0);
  const netIncome       = collectedIncome - totalExpenses;

  const totalUnits     = units.length;
  const occupiedUnits  = units.filter(u => (u.status || '').toUpperCase() === 'OCCUPIED').length;
  const availableUnits = units.filter(u => (u.status || '').toUpperCase() === 'AVAILABLE').length;

  function getCategoryStyle(category) {
    if (category === 'Repairs')   return { bg: 'var(--color-danger-light)', color: 'var(--color-danger-strong)' };
    if (category === 'Cleaning')  return { bg: 'var(--color-info-light)', color: 'var(--color-info-strong)' };
    if (category === 'Utilities') return { bg: 'var(--color-warning-soft)', color: 'var(--color-warning)' };
    if (category === 'Security')  return { bg: 'var(--color-accent-purple-light)', color: 'var(--color-accent-purple)' };
    return                               { bg: 'var(--color-bg-alt)', color: 'var(--color-ink-soft)'    };
  }

  function handleLogout() {
    fetch(`${API_BASE}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    }).finally(() => {
      window.location.href = '/login';
    });
  }

  async function loadAgents() {
    try {
      const res = await fetch(`${API_BASE}/api/agents`, { credentials: 'include' });
      const data = await res.json();
      if (res.ok) setAgents(data);
    } catch (err) {
      console.error('Could not load agents:', err);
    }
  }

  async function loadPendingAgents() {
    try {
      const res = await fetch(`${API_BASE}/api/agents/pending`, { credentials: 'include' });
      const data = await res.json();
      if (res.ok) setPendingAgents(data);
    } catch (err) {
      console.error('Could not load pending agents:', err);
    }
  }

  async function handleApproveAgent(userId) {
    try {
      const res = await fetch(`${API_BASE}/api/agents/${userId}/approve`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Could not approve agent.');
        return;
      }
      await loadPendingAgents();
      await loadAgents();
      alert('Agent approved!');
    } catch (err) {
      alert('Could not reach the server.');
    }
  }

  async function handleRejectAgent(userId) {
    const confirmed = window.confirm('Reject and remove this agent applicant?');
    if (!confirmed) return;

    try {
      const res = await fetch(`${API_BASE}/api/agents/${userId}/reject`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Could not reject agent.');
        return;
      }
      await loadPendingAgents();
      alert('Agent applicant rejected.');
    } catch (err) {
      alert('Could not reach the server.');
    }
  }

  async function handleSaveDisplayName() {
    setSavingDisplayName(true);
    try {
      const res = await fetch(`${API_BASE}/api/landlords/me/display-name`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ display_name: displayNameInput.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Could not update display name.');
        return;
      }
      setLandlord((prev) => (prev ? { ...prev, display_name: data.display_name } : prev));
      setEditingDisplayName(false);
    } catch (err) {
      alert('Could not reach the server.');
    } finally {
      setSavingDisplayName(false);
    }
  }

  async function handleSavePhone() {
    if (!phoneInput.trim()) {
      alert('Enter a valid phone number.');
      return;
    }
    setSavingPhone(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/me/phone`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ phone: phoneInput.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Could not update phone number.');
        return;
      }
      setLandlord((prev) => (prev ? { ...prev, phone: data.phone } : prev));
      setEditingPhone(false);
    } catch (err) {
      alert('Could not reach the server.');
    } finally {
      setSavingPhone(false);
    }
  }

  async function handleSaveEmail() {
    if (!emailInput.trim()) {
      alert('Enter a valid email address.');
      return;
    }
    setSavingEmail(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/me/email`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: emailInput.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Could not update email.');
        return;
      }
      setLandlord((prev) => (prev ? { ...prev, email: data.email } : prev));
      setEditingEmail(false);
    } catch (err) {
      alert('Could not reach the server.');
    } finally {
      setSavingEmail(false);
    }
  }

  async function handleSaveIdNumber() {
    if (!idNumberInput.trim()) {
      alert('Enter a valid ID/passport number.');
      return;
    }
    setSavingIdNumber(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/me/id-number`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id_number: idNumberInput.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Could not update ID number.');
        return;
      }
      setLandlord((prev) => (prev ? { ...prev, id_number: data.id_number } : prev));
      setEditingIdNumber(false);
    } catch (err) {
      alert('Could not reach the server.');
    } finally {
      setSavingIdNumber(false);
    }
  }

  async function handleCreateAgent() {
    if (!newAgentName || !newAgentUsername || !newAgentPhone || !newAgentPassword) {
      setCreateAgentError('Name, username, phone and password are all required.');
      return;
    }
    setCreateAgentError('');
    setCreatingAgent(true);
    try {
      const res = await fetch(`${API_BASE}/api/agents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          full_name: newAgentName,
          username: newAgentUsername,
          phone: newAgentPhone,
          id_number: newAgentIdNumber || undefined,
          password: newAgentPassword,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setCreateAgentError(data.error || 'Could not create agent.');
        return;
      }
      setNewAgentName('');
      setNewAgentUsername('');
      setNewAgentPhone('');
      setNewAgentIdNumber('');
      setNewAgentPassword('');
      await loadAgents();
      alert(data.message);
    } catch (err) {
      setCreateAgentError('Could not reach the server.');
    } finally {
      setCreatingAgent(false);
    }
  }

  function startEditAgent(agent) {
    setEditingAgentId(agent.user_id);
    setEditAgentName(agent.full_name || '');
    setEditAgentPhone(agent.phone || '');
    setEditAgentIdNumber(agent.id_number || '');
    setEditAgentEmail(agent.email || '');
  }

  async function handleSaveAgentEdit(userId) {
    setSavingAgentEdit(true);
    try {
      const res = await fetch(`${API_BASE}/api/agents/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          full_name: editAgentName,
          phone: editAgentPhone,
          id_number: editAgentIdNumber,
          email: editAgentEmail,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Could not update agent.');
        return;
      }
      setEditingAgentId(null);
      await loadAgents();
    } catch (err) {
      alert('Could not reach the server.');
    } finally {
      setSavingAgentEdit(false);
    }
  }

  async function loadCaretakers() {
    try {
      const res = await fetch(`${API_BASE}/api/caretakers`, { credentials: 'include' });
      const data = await res.json();
      if (res.ok) setCaretakers(data);
    } catch (err) {
      console.error('Could not load caretakers:', err);
    }
  }

  async function handleCreateCaretaker() {
    if (!newCaretakerName || !newCaretakerPhone) {
      setCreateCaretakerError('Name and phone are required.');
      return;
    }
    if (!newCaretakerPhoto) {
      setCreateCaretakerError("Please choose a photo of the caretaker's ID or passport.");
      return;
    }
    if (!newCaretakerAgreed) {
      setCreateCaretakerError('Please confirm the caretaker has read and agreed to the terms.');
      return;
    }
    setCreateCaretakerError('');
    setCreatingCaretaker(true);
    try {
      const formData = new FormData();
      formData.append('full_name', newCaretakerName);
      formData.append('phone', newCaretakerPhone);
      if (newCaretakerIdNumber) formData.append('id_number', newCaretakerIdNumber);
      formData.append('id_photo', newCaretakerPhoto);
      formData.append('agreed', 'true');

      const res = await fetch(`${API_BASE}/api/caretakers`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) {
        setCreateCaretakerError(data.error || 'Could not create caretaker.');
        return;
      }
      setNewCaretakerName('');
      setNewCaretakerPhone('');
      setNewCaretakerIdNumber('');
      setNewCaretakerPhoto(null);
      setNewCaretakerAgreed(false);
      await loadCaretakers();
      alert(data.message);
    } catch (err) {
      setCreateCaretakerError('Could not reach the server.');
    } finally {
      setCreatingCaretaker(false);
    }
  }

  async function handleDeleteCaretaker(caretaker) {
    const confirmed = window.confirm(`Remove ${caretaker.full_name} as caretaker?`);
    if (!confirmed) return;

    try {
      const res = await fetch(`${API_BASE}/api/caretakers/${caretaker.caretaker_id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Could not remove caretaker.');
        return;
      }
      await loadCaretakers();
    } catch (err) {
      alert('Could not reach the server.');
    }
  }

  async function handleDownloadCaretakerAgreement(caretaker) {
    try {
      const res = await fetch(`${API_BASE}/api/caretakers/${caretaker.caretaker_id}/agreement/pdf`, {
        credentials: 'include',
      });
      if (!res.ok) {
        alert('Could not generate the agreement PDF.');
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `caretaker-agreement-${caretaker.caretaker_id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Could not reach the server.');
    }
  }

  async function handleDownloadPDF() {
    if (reportStartDate > reportEndDate) {
      alert('Start date must not be after end date.');
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/api/reports/pdf?start_date=${reportStartDate}&end_date=${reportEndDate}`, {
        credentials: 'include',
      });
      if (!res.ok) {
        alert('Could not generate PDF report.');
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rental-ace-report-${reportStartDate}-to-${reportEndDate}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Could not reach the server.');
    }
  }

  async function handleDownloadExcel() {
    if (reportStartDate > reportEndDate) {
      alert('Start date must not be after end date.');
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/api/reports/excel?start_date=${reportStartDate}&end_date=${reportEndDate}`, {
        credentials: 'include',
      });
      if (!res.ok) {
        alert('Could not generate Excel report.');
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rental-ace-report-${reportStartDate}-to-${reportEndDate}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Could not reach the server.');
    }
  }

  async function loadPayments() {
    try {
      const res = await fetch(`${API_BASE}/api/payments`, { credentials: 'include' });
      const data = await res.json();
      if (res.ok) setAllPayments(data);
    } catch (err) {
      console.error('Could not load payments:', err);
    }
  }

  function handleManualUnitChange(unitId) {
    setManualUnitId(unitId);
    // Pre-fill from the unit's active tenant's own saved phone (captured at
    // registration) instead of making staff retype a number they already
    // have on file — still editable afterward, e.g. if the payment actually
    // came from someone else's phone.
    const occupant = tenants.find((t) => String(t.unit_id) === String(unitId) && t.status === 'active');
    setManualPhone(occupant && occupant.phone ? occupant.phone : '');
  }

  async function handleRecordManualPayment() {
    if (!manualUnitId || !manualAmount || !manualDate || !manualMonth) {
      setManualError('Unit, amount, date, and month are all required.');
      return;
    }
    if ((manualMethod === 'mpesa' || manualMethod === 'bank') && !manualPhone) {
      setManualError('Phone used is required for M-Pesa/bank transfer payments.');
      return;
    }
    if ((manualMethod === 'mpesa' || manualMethod === 'bank' || manualMethod === 'cheque') && !manualMpesaCode.trim()) {
      setManualError('Enter the M-Pesa code, bank reference, or cheque number.');
      return;
    }
    setManualError('');
    setSubmittingManual(true);
    try {
      const res = await fetch(`${API_BASE}/api/payments/manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          unit_id: parseInt(manualUnitId),
          amount: parseFloat(manualAmount),
          payment_method: manualMethod,
          mpesa_code: manualMpesaCode || undefined,
          phone_used: manualPhone || undefined,
          payment_date: manualDate,
          month: manualMonth,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setManualError(data.error || 'Could not record payment.');
        return;
      }
      await loadPayments();
      setManualUnitId('');
      setManualAmount('');
      setManualMethod('mpesa');
      setManualPhone('');
      setManualMpesaCode('');

      if (data.allocations && data.allocations.length > 0) {
        const summary = data.allocations
          .map((a) => `${a.invoice_no} (${a.month}): Ksh ${a.amount_applied.toLocaleString()} — ${a.status.toUpperCase()}`)
          .join('\n');
        alert(`Payment recorded. Applied to:\n${summary}`);
      } else {
        alert('Payment recorded, but no outstanding invoice was found to apply it to.');
      }
    } catch (err) {
      setManualError('Could not reach the server.');
    } finally {
      setSubmittingManual(false);
    }
  }

  if (loading) return <div style={styles.centered}><p>Loading...</p></div>;
  if (error)
    return (
      <div style={styles.centered}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: 'var(--color-danger-strong)' }}>⚠ {error}</p>
          <a href="/login" style={{ fontSize: '14px', fontWeight: 700, color: 'var(--color-primary)' }}>← Back to Login</a>
        </div>
      </div>
    );

  const activeNavItem = landlordNav.flatMap((g) => g.items).find((i) => i.key === activePage);

  return (
    <AppShell
      navGroups={landlordNav}
      activeKey={activePage}
      onNavigate={setActivePage}
      badgeCounts={{ agents: pendingAgents.length }}
      userName={landlord?.full_name}
      userRole="Landlord"
      onLogout={handleLogout}
      pageTitle={activeNavItem?.label || 'Dashboard'}
      breadcrumb={properties.length > 0 ? properties.map((p) => `${p.name} · ${p.location}`).join(' · ') : 'Loading property...'}
      notificationCount={pendingAgents.length}
      onNotificationsClick={() => setActivePage('agents')}
    >
      <div>

        {/* DASHBOARD PAGE */}
        {activePage === 'dashboard' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

            <div style={styles.statRow}>
              <StatCard label="Total Units" value={totalUnits} icon={Building2} />
              <StatCard
                label="Occupied"
                value={occupiedUnits}
                icon={CheckCircle2}
                tone="good"
                subtext={totalUnits > 0 ? `${Math.round((occupiedUnits / totalUnits) * 100)}% of ${totalUnits} occupied` : undefined}
                progress={totalUnits > 0 ? Math.round((occupiedUnits / totalUnits) * 100) : 0}
              />
              <StatCard label="Available" value={availableUnits} icon={Building2} />
              <StatCard
                label={`Collected (${selectedMonth})`}
                value={`KSh ${collectedIncome.toLocaleString()}`}
                icon={Wallet}
                tone="good"
              />
              <StatCard
                label="Outstanding"
                value={`KSh ${outstanding.toLocaleString()}`}
                icon={AlertTriangle}
                tone={outstanding > 0 ? 'warning' : 'default'}
              />
            </div>

            <SectionCard title="Units Overview">
              <DataTable
                columns={[
                  { key: 'unit_number', label: 'House', render: (u) => `House ${u.unit_number}` },
                  {
                    key: 'tenant',
                    label: 'Tenant',
                    render: (u) => {
                      const occupant = tenants.find((t) => t.unit_id === u.unit_id && t.status === 'active');
                      return occupant ? occupant.full_name : '—';
                    },
                  },
                  { key: 'rent_amount', label: 'Rent', render: (u) => `KSh ${u.rent_amount.toLocaleString()}` },
                  {
                    key: 'payment_status',
                    label: `${selectedMonth} Status`,
                    render: (u) => {
                      const payment = allPayments.find((p) => p.unit_number === u.unit_number && p.month === selectedMonth);
                      return payment ? <StatusBadge status={payment.status} /> : <StatusBadge status={u.status} />;
                    },
                  },
                ]}
                rows={units}
                rowKey={(u) => u.unit_id}
                emptyIcon={Building2}
                emptyTitle="No units yet"
              />
            </SectionCard>

          </div>
        )}

        {/* PROPERTIES PAGE */}
        {activePage === 'properties' && (
          <div style={styles.content}>
            <div style={styles.card}>
              <p style={styles.cardTitle}>Your Account Details</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '14px' }}>
                <p style={styles.tenantDetails} >Phone</p>
                {editingPhone ? (
                  <>
                    <input
                      type="text"
                      value={phoneInput}
                      onChange={(e) => setPhoneInput(e.target.value)}
                      placeholder={landlord?.phone}
                      style={styles.formInput}
                    />
                    <button onClick={handleSavePhone} disabled={savingPhone} style={styles.approveBtn}>
                      {savingPhone ? 'Saving...' : 'Save'}
                    </button>
                    <button onClick={() => setEditingPhone(false)} style={styles.rejectBtn}>Cancel</button>
                  </>
                ) : (
                  <>
                    <p style={{ ...styles.unitTenant, margin: 0 }}>{landlord?.phone || '—'}</p>
                    <button
                      onClick={() => { setPhoneInput(landlord?.phone || ''); setEditingPhone(true); }}
                      style={styles.approveBtn}
                    >
                      Change
                    </button>
                  </>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '14px' }}>
                <p style={styles.tenantDetails}>ID / Passport Number</p>
                {editingIdNumber ? (
                  <>
                    <input
                      type="text"
                      value={idNumberInput}
                      onChange={(e) => setIdNumberInput(e.target.value)}
                      placeholder={landlord?.id_number}
                      style={styles.formInput}
                    />
                    <button onClick={handleSaveIdNumber} disabled={savingIdNumber} style={styles.approveBtn}>
                      {savingIdNumber ? 'Saving...' : 'Save'}
                    </button>
                    <button onClick={() => setEditingIdNumber(false)} style={styles.rejectBtn}>Cancel</button>
                  </>
                ) : (
                  <>
                    <p style={{ ...styles.unitTenant, margin: 0 }}>{landlord?.id_number || '—'}</p>
                    <button
                      onClick={() => { setIdNumberInput(landlord?.id_number || ''); setEditingIdNumber(true); }}
                      style={styles.approveBtn}
                    >
                      Change
                    </button>
                  </>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <p style={styles.tenantDetails}>Email</p>
                {editingEmail ? (
                  <>
                    <input
                      type="email"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      placeholder={landlord?.email || 'you@example.com'}
                      style={styles.formInput}
                    />
                    <button onClick={handleSaveEmail} disabled={savingEmail} style={styles.approveBtn}>
                      {savingEmail ? 'Saving...' : 'Save'}
                    </button>
                    <button onClick={() => setEditingEmail(false)} style={styles.rejectBtn}>Cancel</button>
                  </>
                ) : (
                  <>
                    <p style={{ ...styles.unitTenant, margin: 0 }}>{landlord?.email || '— (add one to receive receipts/notifications)'}</p>
                    <button
                      onClick={() => { setEmailInput(landlord?.email || ''); setEditingEmail(true); }}
                      style={styles.approveBtn}
                    >
                      Change
                    </button>
                  </>
                )}
              </div>
            </div>

            <div style={styles.card}>
              <p style={styles.cardTitle}>Name on Tenancy Agreements</p>
              <p style={{ fontSize: '13px', color: 'var(--color-muted)', margin: '-8px 0 16px' }}>
                This is how you're identified as "the Landlord" on tenant contracts. Set a
                business/property name here if you'd rather not use your own account name.
              </p>
              {editingDisplayName ? (
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    value={displayNameInput}
                    onChange={(e) => setDisplayNameInput(e.target.value)}
                    placeholder={landlord?.full_name}
                    style={{ ...styles.unitOnboarding, flex: 1, minWidth: '200px', padding: '10px 14px', border: '1.5px solid var(--color-border)', borderRadius: '8px', fontSize: '14px', color: 'var(--color-ink)', backgroundColor: 'var(--color-surface)' }}
                  />
                  <button onClick={handleSaveDisplayName} disabled={savingDisplayName} style={styles.approveBtn}>
                    {savingDisplayName ? 'Saving...' : 'Save'}
                  </button>
                  <button onClick={() => setEditingDisplayName(false)} style={styles.rejectBtn}>Cancel</button>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <p style={{ ...styles.unitTenant, margin: 0 }}>
                    {landlord?.display_name || landlord?.full_name}
                  </p>
                  <button
                    onClick={() => { setDisplayNameInput(landlord?.display_name || ''); setEditingDisplayName(true); }}
                    style={styles.approveBtn}
                  >
                    Change
                  </button>
                </div>
              )}
            </div>

            <div style={styles.card}>
              <p style={styles.cardTitle}>Your Properties ({properties.length})</p>
              {properties.length === 0 ? (
                <p style={styles.placeholderText}>No properties on record.</p>
              ) : (
                properties.map((p) => (
                  <div key={p.property_id} style={styles.unitRow}>
                    <div style={styles.unitLeft}>
                      <div style={styles.unitBadge}>🏠</div>
                      <div>
                        <p style={styles.unitTenant}>{p.name}</p>
                        <p style={styles.unitRent}>{p.location}</p>
                        <p style={styles.unitOnboarding}>
                          Paybill {p.paybill_no || '—'} · Acc {p.account_no}
                        </p>
                      </div>
                    </div>
                    <p style={{ fontSize: '13px', fontWeight: 700, color: GREEN, margin: 0 }}>
                      {units.filter((u) => u.property_id === p.property_id).length} unit(s)
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* AGENTS PAGE */}
        {activePage === 'agents' && (
          <div style={styles.content}>
            <div style={styles.card}>
              <p style={styles.cardTitle}>Add an Agent Directly</p>
              <p style={{ fontSize: '13px', color: 'var(--color-muted)', margin: '-8px 0 16px' }}>
                Creates the account already active on your property — no need to wait for them
                to self-register and get approved. Useful when the admin isn't around.
              </p>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
                <input type="text" value={newAgentName} onChange={(e) => setNewAgentName(e.target.value)} placeholder="Full name" style={styles.formInput} />
                <input type="text" value={newAgentUsername} onChange={(e) => setNewAgentUsername(e.target.value)} placeholder="Username" style={styles.formInput} />
                <input type="text" value={newAgentPhone} onChange={(e) => setNewAgentPhone(e.target.value)} placeholder="Phone" style={styles.formInput} />
                <input type="text" value={newAgentIdNumber} onChange={(e) => setNewAgentIdNumber(e.target.value)} placeholder="ID number (optional)" style={styles.formInput} />
                <input type="password" value={newAgentPassword} onChange={(e) => setNewAgentPassword(e.target.value)} placeholder="Password" style={styles.formInput} />
              </div>
              <button onClick={handleCreateAgent} disabled={creatingAgent} style={styles.approveBtn}>
                {creatingAgent ? 'Creating...' : '+ Create Agent'}
              </button>
              {createAgentError !== '' && (
                <p style={{ color: 'var(--color-danger-strong)', fontSize: '13px', margin: '12px 0 0' }}>{createAgentError}</p>
              )}
            </div>

            <div style={styles.card}>
              <p style={styles.cardTitle}>Pending Agents ({pendingAgents.length})</p>
              {pendingAgents.length === 0 ? (
                <p style={styles.placeholderText}>No pending agent applications.</p>
              ) : (
                pendingAgents.map((agent) => (
                  <div key={agent.user_id} style={styles.tenantRow}>
                    <div style={styles.unitBadge}>🧑‍💼</div>
                    <div style={styles.tenantInfo}>
                      <p style={styles.tenantName}>{agent.full_name}</p>
                      <p style={styles.tenantDetails}>@{agent.username} · {agent.phone}</p>
                    </div>
                    <div style={styles.tenantActions}>
                      <button onClick={() => handleApproveAgent(agent.user_id)} style={styles.approveBtn}>Approve</button>
                      <button onClick={() => handleRejectAgent(agent.user_id)} style={styles.rejectBtn}>Reject</button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div style={styles.card}>
              <p style={styles.cardTitle}>Active Agents ({agents.length})</p>
              {agents.length === 0 ? (
                <p style={styles.placeholderText}>No agents on your property yet.</p>
              ) : (
                agents.map((agent) => (
                  <div key={agent.user_id} style={styles.tenantRow}>
                    <div style={styles.unitBadge}>🧑‍💼</div>
                    {editingAgentId === agent.user_id ? (
                      <div style={{ flex: 1, display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <input type="text" value={editAgentName} onChange={(e) => setEditAgentName(e.target.value)} placeholder="Full name" style={styles.formInput} />
                        <input type="text" value={editAgentPhone} onChange={(e) => setEditAgentPhone(e.target.value)} placeholder="Phone" style={styles.formInput} />
                        <input type="text" value={editAgentIdNumber} onChange={(e) => setEditAgentIdNumber(e.target.value)} placeholder="ID number" style={styles.formInput} />
                        <input type="email" value={editAgentEmail} onChange={(e) => setEditAgentEmail(e.target.value)} placeholder="Email" style={styles.formInput} />
                        <button onClick={() => handleSaveAgentEdit(agent.user_id)} disabled={savingAgentEdit} style={styles.approveBtn}>
                          {savingAgentEdit ? 'Saving...' : 'Save'}
                        </button>
                        <button onClick={() => setEditingAgentId(null)} style={styles.rejectBtn}>Cancel</button>
                      </div>
                    ) : (
                      <>
                        <div style={styles.tenantInfo}>
                          <p style={styles.tenantName}>{agent.full_name}</p>
                          <p style={styles.tenantDetails}>@{agent.username} · {agent.phone}{agent.email ? ` · ${agent.email}` : ''}</p>
                        </div>
                        <div style={styles.tenantActions}>
                          <button onClick={() => startEditAgent(agent)} style={styles.approveBtn}>Edit</button>
                        </div>
                      </>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* CARETAKERS PAGE */}
        {activePage === 'caretakers' && (
          <div style={styles.content}>
            <div style={styles.card}>
              <p style={styles.cardTitle}>Add a Caretaker</p>
              <p style={{ fontSize: '13px', color: 'var(--color-muted)', margin: '-8px 0 16px' }}>
                Caretakers don't get a login — this creates their record and signs their
                agreement in one step, with them present. Review the terms with them, take a
                photo of their ID/passport, then confirm below.
              </p>

              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
                <input type="text" value={newCaretakerName} onChange={(e) => setNewCaretakerName(e.target.value)} placeholder="Full name" style={styles.formInput} />
                <input type="text" value={newCaretakerPhone} onChange={(e) => setNewCaretakerPhone(e.target.value)} placeholder="Phone" style={styles.formInput} />
                <input type="text" value={newCaretakerIdNumber} onChange={(e) => setNewCaretakerIdNumber(e.target.value)} placeholder="ID number (optional)" style={styles.formInput} />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <p style={styles.tenantDetails}>ID / Passport Photo</p>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setNewCaretakerPhoto(e.target.files && e.target.files[0])}
                />
              </div>

              {caretakerTemplate && (
                <div style={{ maxHeight: '220px', overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: '8px', padding: '14px', marginBottom: '16px', backgroundColor: 'var(--color-bg-alt)' }}>
                  {caretakerTemplate.split('\n\n').map((p, i) => (
                    <p key={i} style={{ fontSize: '12px', color: 'var(--color-ink-soft)', lineHeight: 1.6, whiteSpace: 'pre-line', margin: '0 0 10px' }}>{p.trim()}</p>
                  ))}
                </div>
              )}

              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '13px', color: 'var(--color-ink)', marginBottom: '16px', cursor: 'pointer' }}>
                <input type="checkbox" checked={newCaretakerAgreed} onChange={(e) => setNewCaretakerAgreed(e.target.checked)} style={{ marginTop: '2px' }} />
                I confirm the caretaker has read and agreed to the terms above.
              </label>

              <button onClick={handleCreateCaretaker} disabled={creatingCaretaker} style={styles.approveBtn}>
                {creatingCaretaker ? 'Creating...' : '+ Add Caretaker'}
              </button>
              {createCaretakerError !== '' && (
                <p style={{ color: 'var(--color-danger-strong)', fontSize: '13px', margin: '12px 0 0' }}>{createCaretakerError}</p>
              )}
            </div>

            <div style={styles.card}>
              <p style={styles.cardTitle}>Your Caretakers ({caretakers.length})</p>
              {caretakers.length === 0 ? (
                <p style={styles.placeholderText}>No caretakers on record.</p>
              ) : (
                caretakers.map((c) => (
                  <div key={c.caretaker_id} style={styles.tenantRow}>
                    <div style={styles.unitBadge}>🧹</div>
                    <div style={styles.tenantInfo}>
                      <p style={styles.tenantName}>{c.full_name}</p>
                      <p style={styles.tenantDetails}>{c.phone}</p>
                      <p style={styles.tenantPenalty}>Signed {c.agreement_signed_at}</p>
                    </div>
                    <div style={styles.tenantActions}>
                      <button onClick={() => handleDownloadCaretakerAgreement(c)} style={styles.approveBtn}>📄 Agreement</button>
                      <button onClick={() => handleDeleteCaretaker(c)} style={styles.rejectBtn}>Remove</button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* PAYMENTS PAGE */}
        {activePage === 'payments' && (
          <div style={styles.content}>
            <div style={styles.card}>
              <p style={styles.cardTitle}>Record Manual Payment</p>
              <p style={{ fontSize: '13px', color: 'var(--color-muted)', margin: '-8px 0 16px' }}>
                For cash, bank transfer, M-Pesa paid straight to a personal phone, or a tenant
                paying their rent in installments (e.g. part this week, the rest later) — record
                each installment separately and it adds up against their invoice automatically.
              </p>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
                <select
                  value={manualUnitId}
                  onChange={(e) => handleManualUnitChange(e.target.value)}
                  style={styles.formInput}
                >
                  <option value="">Select a unit...</option>
                  {units.map((u) => (
                    <option key={u.unit_id} value={u.unit_id}>
                      House {u.unit_number}{u.has_water_bill ? ' 💧' : ''}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  value={manualAmount}
                  onChange={(e) => setManualAmount(e.target.value)}
                  placeholder="Amount (Ksh), e.g. 3000"
                  style={styles.formInput}
                  min="1"
                />
                <select
                  value={manualMethod}
                  onChange={(e) => setManualMethod(e.target.value)}
                  style={styles.formInput}
                >
                  <option value="mpesa">M-Pesa (personal phone)</option>
                  <option value="bank">Bank Transfer</option>
                  <option value="cash">Cash</option>
                  <option value="cheque">Cheque</option>
                </select>
                {(manualMethod === 'mpesa' || manualMethod === 'bank') && (
                  <input
                    type="text"
                    value={manualPhone}
                    onChange={(e) => setManualPhone(e.target.value)}
                    placeholder="Phone used, e.g. 0712345678"
                    style={styles.formInput}
                  />
                )}
                {manualMethod !== 'cash' && (
                  <input
                    type="text"
                    value={manualMpesaCode}
                    onChange={(e) => setManualMpesaCode(e.target.value)}
                    placeholder={manualMethod === 'cheque' ? 'Cheque number' : 'M-Pesa code / reference'}
                    style={styles.formInput}
                  />
                )}
                <input
                  type="date"
                  value={manualDate}
                  onChange={(e) => setManualDate(e.target.value)}
                  style={styles.formInput}
                />
                <input
                  type="text"
                  value={manualMonth}
                  onChange={(e) => setManualMonth(e.target.value)}
                  placeholder="Month, e.g. November 2026"
                  style={styles.formInput}
                />
              </div>
              <button onClick={handleRecordManualPayment} disabled={submittingManual} style={styles.approveBtn}>
                {submittingManual ? 'Recording...' : '+ Record Payment'}
              </button>
              {manualError !== '' && (
                <p style={{ color: 'var(--color-danger-strong)', fontSize: '13px', margin: '12px 0 0' }}>{manualError}</p>
              )}
            </div>

            <div style={styles.card}>
              <p style={styles.cardTitle}>All Payments ({allPayments.length})</p>
              {allPayments.length === 0 ? (
                <p style={styles.placeholderText}>No payments recorded yet.</p>
              ) : (
                allPayments.map((payment) => (
                  <div key={payment.payment_id} style={styles.unitRow}>
                    <div style={styles.unitLeft}>
                      <div style={styles.unitBadge}>H{payment.unit_number}</div>
                      <div>
                        <p style={styles.unitTenant}>{payment.tenant_name}</p>
                        <p style={styles.unitRent}>Ksh {payment.amount.toLocaleString()} · {payment.month} · {payment.payment_date}</p>
                      </div>
                    </div>
                    <span style={{
                      fontSize: '12px',
                      fontWeight: 700,
                      padding: '4px 12px',
                      borderRadius: '20px',
                      backgroundColor: payment.status === 'paid' ? 'var(--color-primary-light)' : 'var(--color-danger-light)',
                      color: payment.status === 'paid' ? GREEN : 'var(--color-danger-strong)',
                    }}>
                      {payment.status.toUpperCase()}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* WATER BILLS PAGE */}
        {activePage === 'water' && (
          <div style={styles.content}>
            <div style={styles.card}>
              <p style={styles.cardTitle}>Water Bill History ({waterHistory.length})</p>
              {waterHistory.length === 0 ? (
                <p style={styles.placeholderText}>No water bills recorded yet.</p>
              ) : (
                waterHistory.map((bill) => (
                  <div key={bill.bill_id} style={styles.unitRow}>
                    <div style={styles.unitLeft}>
                      <div style={styles.unitBadge}>H{bill.unit_number}</div>
                      <div>
                        <p style={styles.unitTenant}>{bill.month} {bill.year}</p>
                        <p style={styles.unitRent}>Ksh {bill.amount.toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* REPORTS PAGE */}
        {activePage === 'reports' && (
          <div style={styles.content}>

            {/* Month Selector */}
            <div style={styles.card}>
              <div style={styles.monthRow}>
                <div>
                  <p style={styles.cardTitle}>Financial Report</p>
                  <p style={{ fontSize: '13px', color: 'var(--color-muted)', margin: 0 }}>
                    Select a month to view its report
                  </p>
                </div>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  style={styles.monthSelect}
                >
                  {availableMonths.map((month) => (
                    <option key={month} value={month}>{month}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Income Summary Cards */}
            <div style={styles.summaryRow}>
              <div style={styles.summaryCard}>
                <p style={{ ...styles.summaryValue, color: 'var(--color-ink)' }}>
                  Ksh {expectedIncome.toLocaleString()}
                </p>
                <p style={styles.summaryLabel}>Expected</p>
              </div>
              <div style={styles.summaryCard}>
                <p style={{ ...styles.summaryValue, color: 'var(--color-brand)' }}>
                  Ksh {collectedIncome.toLocaleString()}
                </p>
                <p style={styles.summaryLabel}>Collected</p>
              </div>
              <div style={styles.summaryCard}>
                <p style={{ ...styles.summaryValue, color: 'var(--color-danger-strong)' }}>
                  Ksh {outstanding.toLocaleString()}
                </p>
                <p style={styles.summaryLabel}>Outstanding</p>
              </div>
            </div>

            {/* Income vs Expenses + Unit Breakdown */}
            <div style={isDesktop ? styles.twoColRow : {}}>

              <div style={{ flex: 1 }}>
                {/* Net Income Card */}
                <div style={styles.card}>
                  <p style={styles.cardTitle}>Income vs Expenses</p>

                  <div style={styles.reportRow}>
                    <p style={styles.reportLabel}>Collected Income</p>
                    <p style={{ ...styles.reportValue, color: 'var(--color-brand)' }}>
                      Ksh {collectedIncome.toLocaleString()}
                    </p>
                  </div>
                  <div style={styles.reportRow}>
                    <p style={styles.reportLabel}>Total Expenses</p>
                    <p style={{ ...styles.reportValue, color: 'var(--color-danger-strong)' }}>
                      Ksh {totalExpenses.toLocaleString()}
                    </p>
                  </div>
                  <div style={{ ...styles.reportRow, borderTop: '2px solid var(--color-border-soft)', paddingTop: '16px', marginTop: '8px' }}>
                    <p style={{ ...styles.reportLabel, fontWeight: 700, color: 'var(--color-ink)', fontSize: '15px' }}>
                      Net Income
                    </p>
                    <p style={{ ...styles.reportValue, color: 'var(--color-brand)', fontSize: '20px', fontWeight: 700 }}>
                      Ksh {netIncome.toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* Expenses Breakdown */}
                <div style={styles.card}>
                  <p style={styles.cardTitle}>Expenses Breakdown</p>
                  {monthExpenses.length === 0 ? (
                    <p style={styles.placeholderText}>No expenses for {selectedMonth}</p>
                  ) : (
                    monthExpenses.map((expense) => {
                      const catStyle = getCategoryStyle(expense.category);
                      return (
                        <div key={expense.expense_id} style={styles.expenseRow}>
                          <span style={{
                            ...styles.categoryBadge,
                            backgroundColor: catStyle.bg,
                            color: catStyle.color,
                          }}>
                            {expense.category}
                          </span>
                          <div style={{ flex: 1 }}>
                            <p style={styles.expenseDesc}>{expense.description}</p>
                          </div>
                          <p style={styles.expenseAmount}>
                            Ksh {expense.amount.toLocaleString()}
                          </p>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Unit Breakdown */}
              <div style={{ flex: 1 }}>
                <div style={styles.card}>
                  <p style={styles.cardTitle}>Unit Breakdown — {selectedMonth}</p>
                  {units.map((unit) => {
                    const occupant = tenants.find((t) => t.unit_id === unit.unit_id && t.status === 'active');
                    const payment = monthPayments.find((p) => p.unit_number === unit.unit_number);
                    return (
                      <div key={unit.unit_id} style={styles.unitRow}>
                        <div style={styles.unitLeft}>
                          <div style={styles.unitBadge}>H{unit.unit_number}</div>
                          <div>
                            <p style={styles.unitTenant}>{occupant ? occupant.full_name : 'Available'}</p>
                            <p style={styles.unitRent}>Ksh {unit.rent_amount.toLocaleString()}</p>
                          </div>
                        </div>
                        <span style={{
                          fontSize: '12px',
                          fontWeight: 700,
                          padding: '4px 12px',
                          borderRadius: '20px',
                          backgroundColor: payment?.status === 'paid' ? 'var(--color-primary-light)' : 'var(--color-danger-light)',
                          color: payment?.status === 'paid' ? 'var(--color-brand)' : 'var(--color-danger-strong)',
                        }}>
                          {payment ? payment.status.toUpperCase() : '——'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

            </div>

            {/* Report Timeframe + Download Buttons */}
            <div style={styles.card}>
              <div style={styles.monthRow}>
                <div>
                  <p style={styles.cardTitle}>Report Timeframe</p>
                  <p style={{ fontSize: '13px', color: 'var(--color-muted)', margin: 0 }}>
                    Choose the date range the PDF/Excel report should cover
                  </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <input
                    type="date"
                    value={reportStartDate}
                    onChange={(e) => setReportStartDate(e.target.value)}
                    style={styles.monthSelect}
                  />
                  <span style={{ color: 'var(--color-muted)', fontSize: '13px' }}>to</span>
                  <input
                    type="date"
                    value={reportEndDate}
                    onChange={(e) => setReportEndDate(e.target.value)}
                    style={styles.monthSelect}
                  />
                </div>
              </div>
            </div>

            <div style={styles.downloadRow}>
              <button onClick={handleDownloadPDF} style={styles.pdfBtn}>
                📄 Download PDF Report
              </button>
              <button onClick={handleDownloadExcel} style={styles.excelBtn}>
                📊 Download Excel Sheet
              </button>
            </div>

          </div>
        )}

      </div>
    </AppShell>
  );
}

const GREEN = 'var(--color-brand)';

const styles = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: 'var(--color-bg-alt)',
    fontFamily: 'var(--font-sans)',
  },
  centered: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
  },
  header: {
    backgroundColor: GREEN,
    color: 'var(--color-text-on-brand)',
    padding: '24px 32px 0',
  },
  headerTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: '20px',
  },
  headerLabel: {
    fontSize: '11px',
    opacity: 0.8,
    margin: '0 0 6px',
    textTransform: 'uppercase',
    letterSpacing: '2px',
  },
  headerName: { fontSize: '22px', fontWeight: 700, margin: '0 0 4px' },
  headerSub: { fontSize: '14px', opacity: 0.8, margin: 0 },
  logoutBtn: {
    padding: '10px 22px',
    background: 'rgba(255,255,255,0.2)',
    border: '1px solid rgba(255,255,255,0.4)',
    borderRadius: '10px',
    color: 'var(--color-text-on-brand)',
    fontSize: '14px',
    cursor: 'pointer',
  },
  nav: {
    display: 'flex',
    gap: '4px',
  },
  navBtn: {
    padding: '12px 24px',
    border: 'none',
    borderRadius: '8px 8px 0 0',
    color: 'var(--color-text-on-brand)',
    fontSize: '14px',
    cursor: 'pointer',
  },
  bodyDesktop: {
    flex: 1,
    padding: '0',
  },
  bodyMobile: {
    flex: 1,
  },
  content: {
    padding: '24px 32px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  twoColRow: {
    display: 'flex',
    gap: '20px',
    flexWrap: 'wrap',
  },
  summaryRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '24px',
  },
  statRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
  },
  summaryCard: {
    flex: '1 1 140px',
    background: 'var(--color-surface)',
    borderRadius: '14px',
    padding: '20px 16px',
    textAlign: 'center',
    boxShadow: 'var(--shadow-sm)',
  },
  summaryValue: {
    fontSize: '24px',
    fontWeight: 700,
    color: GREEN,
    margin: '0 0 6px',
  },
  summaryLabel: {
    fontSize: '12px',
    color: 'var(--color-muted)',
    margin: 0,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  card: {
    background: 'var(--color-surface)',
    borderRadius: '16px',
    marginBottom: '20px',
    padding: '24px',
    boxShadow: 'var(--shadow-sm)',
  },
  cardTitle: {
    fontSize: '13px',
    fontWeight: 700,
    color: 'var(--color-ink)',
    margin: '0 0 20px',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  monthRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px',
  },
  monthSelect: {
    padding: '10px 16px',
    border: '1.5px solid var(--color-border-soft)',
    borderRadius: '10px',
    fontSize: '14px',
    backgroundColor: 'var(--color-surface)',
    color: 'var(--color-ink)',
    cursor: 'pointer',
    outline: 'none',
  },
  reportRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0',
    borderBottom: '1px solid var(--color-border-soft)',
  },
  reportLabel: { fontSize: '14px', color: 'var(--color-ink-soft)', margin: 0 },
  reportValue: { fontSize: '15px', fontWeight: 600, margin: 0 },
  unitRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0',
    borderBottom: '1px solid var(--color-border-soft)',
  },
  unitLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  unitBadge: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    background: 'var(--color-primary-light)',
    color: GREEN,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 700,
    minWidth: '40px',
  },
  unitTenant: { fontSize: '14px', fontWeight: 600, margin: '0 0 2px', color: 'var(--color-ink)' },
  unitRent: { fontSize: '12px', color: 'var(--color-muted)', margin: 0 },
  unitOnboarding: { fontSize: '11px', color: 'var(--color-muted)', margin: '2px 0 0' },
  expenseRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 0',
    borderBottom: '1px solid var(--color-border-soft)',
  },
  categoryBadge: {
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  expenseDesc: { fontSize: '13px', color: 'var(--color-ink-soft)', margin: 0 },
  expenseAmount: { fontSize: '14px', fontWeight: 700, color: 'var(--color-danger-strong)', margin: 0, whiteSpace: 'nowrap' },
  downloadRow: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    marginBottom: '32px',
  },
  pdfBtn: {
    flex: 1,
    padding: '16px',
    backgroundColor: 'var(--color-danger-strong)',
    color: 'var(--color-text-on-brand)',
    border: 'none',
    borderRadius: '12px',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    minWidth: '160px',
  },
  excelBtn: {
    flex: 1,
    padding: '16px',
    backgroundColor: GREEN,
    color: 'var(--color-text-on-brand)',
    border: 'none',
    borderRadius: '12px',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    minWidth: '160px',
  },
  placeholderText: {
    color: 'var(--color-muted)',
    fontSize: '14px',
    textAlign: 'center',
    padding: '32px 0',
  },
  tenantRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    padding: '16px 0',
    borderBottom: '1px solid var(--color-border-soft)',
    flexWrap: 'wrap',
  },
  tenantInfo: { flex: 1, minWidth: '200px' },
  tenantName: { fontSize: '15px', fontWeight: 600, margin: '0 0 4px', color: 'var(--color-ink)' },
  tenantDetails: { fontSize: '13px', color: 'var(--color-muted)', margin: '0 0 4px' },
  tenantPenalty: { fontSize: '12px', color: 'var(--color-warning)', margin: 0 },
  tenantActions: { display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '4px' },
  approveBtn: {
    padding: '10px 18px',
    backgroundColor: GREEN,
    color: 'var(--color-text-on-brand)',
    border: 'none',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  rejectBtn: {
    padding: '10px 18px',
    backgroundColor: 'var(--color-danger-strong)',
    color: 'var(--color-text-on-brand)',
    border: 'none',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  formInput: {
    flex: '1 1 160px',
    padding: '10px 14px',
    border: '1.5px solid var(--color-border)',
    borderRadius: '8px',
    fontSize: '14px',
    color: 'var(--color-ink)',
    backgroundColor: 'var(--color-surface)',
  },
};

export default LandlordDashboard;